import React from 'react';
import {
    Box,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    LocalMall as ShopIcon,
    Restaurant as FnBIcon,
} from '@mui/icons-material';
import { PosSaleSnapshot } from '../../types';
import { formatDateTime } from '../../utils/dateUtils';

interface SaleDetailsProps {
    sale: PosSaleSnapshot;
    /** Staff-only details (cashier) */
    showStaffDetails?: boolean;
}

const money = (n: number | string | undefined | null) =>
    new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(Number(n ?? 0));

function shareLabel(sale: PosSaleSnapshot): string | null {
    const split = sale.split;
    if (!split) return null;
    if (split.mode === 'even' && split.shareIndex && split.shareCount) {
        return `Share ${split.shareIndex} of ${split.shareCount}`;
    }
    if (split.mode === 'remaining') return 'Remaining balance of the tab';
    if (split.mode === 'items') return 'Split by item';
    return null;
}

/**
 * Itemized view of a Shop / F&B sale charged to a member account (Transaction.metadata.sale):
 * source, order / tab, items, subtotal, discount, tax, tip and total.
 */
const SaleDetails: React.FC<SaleDetailsProps> = ({ sale, showStaffDetails = false }) => {
    const isFnB = sale.source === 'fnb';
    const share = shareLabel(sale);
    const items = sale.items ?? [];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Chip
                    icon={isFnB ? <FnBIcon /> : <ShopIcon />}
                    label={isFnB ? 'Food & Beverage' : 'Pro Shop'}
                    color={isFnB ? 'secondary' : 'primary'}
                    size="small"
                />
                {sale.orderNumber && (
                    <Typography variant="body2" color="text.secondary">
                        {isFnB ? `Tab #${sale.orderNumber}` : `Order #${sale.orderNumber}`}
                    </Typography>
                )}
                {share && <Chip label={share} size="small" variant="outlined" />}
                {sale.soldAt && (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        {formatDateTime(sale.soldAt)}
                    </Typography>
                )}
            </Box>

            {items.length > 0 ? (
                <Table size="small" sx={{ mb: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Qty</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item, idx) => (
                            <TableRow key={idx}>
                                <TableCell>
                                    <Typography variant="body2">{item.name}</Typography>
                                    {(item.addOns ?? []).map((a, i) => (
                                        <Typography key={i} variant="caption" color="text.secondary" display="block">
                                            + {a.name} ({money(a.price)})
                                        </Typography>
                                    ))}
                                    {item.notes && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {item.notes}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">{money(item.unitPrice)}</TableCell>
                                <TableCell align="right">
                                    {item.discountAmount > 0 ? `-${money(item.discountAmount)}` : '—'}
                                </TableCell>
                                <TableCell align="right">{money(item.lineTotal)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                share && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {share}{sale.tabLabel ? ` · ${sale.tabLabel}` : ''}
                    </Typography>
                )
            )}

            <Box sx={{ maxWidth: 320, ml: 'auto' }}>
                <Row label="Subtotal" value={money(sale.subtotal)} />
                {sale.discountAmount > 0 && <Row label="Discount" value={`-${money(sale.discountAmount)}`} />}
                <Row label="Tax" value={money(sale.tax)} />
                {sale.tip > 0 && <Row label="Tip" value={money(sale.tip)} />}
                <Divider sx={{ my: 0.5 }} />
                <Row label="Total charged" value={money(sale.total)} bold />
            </Box>

            {showStaffDetails && sale.cashierName && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Rung up by {sale.cashierName}
                </Typography>
            )}
        </Box>
    );
};

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
        <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>{value}</Typography>
    </Box>
);

export default SaleDetails;
